import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { getOfflineActions, saveOfflineAction } from '../utils/offlineStorage';
import { scopeHeaders } from '../utils/scopeHeaders';
import { apiBase } from '../../utils/authHeaders';
import { 
  Calendar, Clock, Users, Plus, Search, Filter,
  CheckCircle, AlertCircle, X, ChevronRight,
  Bed, SprayCan, QrCode, Scan
} from 'lucide-react';

const MobileBookings = () => {
  const location = useLocation();
  const { user, token, role, isOnline, hasPermission } = useMobileAuth();
  const [activeView, setActiveView] = useState('bookings'); // 'bookings', 'housekeeping', 'checkin'
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showScanner, setShowScanner] = useState(false);
  const [offlineActions, setOfflineActions] = useState([]);

  useEffect(() => {
    fetchBookings();
    fetchRooms();
    loadOfflineActions();

    if (location.state?.action === 'new') {
        setShowNewBooking(true);
    }
  }, [location.state]);

  const loadOfflineActions = async () => {
    try {
      const actions = await getOfflineActions();
      setOfflineActions(actions.filter(action => action.type === 'bookings'));
    } catch (error) {
      console.error('Error loading offline actions:', error);
    }
  };

  // ... (keep existing fetch functions) ...
  const fetchBookings = async () => {
    try {
      
      const response = await fetch(`${apiBase()}/api/mobile/bookings`, {
        headers: scopeHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      
      const response = await fetch(`${apiBase()}/api/mobile/rooms`, {
        headers: scopeHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setRooms(data);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const updateRoomStatus = async (roomId, status) => {
    // Optimistic update
    setRooms(rooms.map(r => r.id === roomId ? { ...r, status } : r));
    
    const action = {
        id: Date.now().toString(),
        type: 'bookings',
        subType: 'room_status',
        roomId,
        status,
        timestamp: new Date().toISOString(),
        synced: false
    };

    if (isOnline) {
        try {
          
          await fetch(`${apiBase()}/api/mobile/rooms/${roomId}/status`, {
            method: 'PUT',
            headers: {
              ...scopeHeaders(token),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status })
          });
        } catch (error) {
          console.error('Error updating room status:', error);
          saveOfflineAction(action);
          setOfflineActions([...offlineActions, action]);
        }
    } else {
        saveOfflineAction(action);
        setOfflineActions([...offlineActions, action]);
    }
  };

  const handleCheckInScan = (data) => {
    // Simulate QR scan processing
    const booking = bookings.find(b => b.id === data || b.confirmationCode === data);
    if (booking) {
      setSelectedBooking(booking);
      setShowScanner(false);
    } else {
      alert('Booking not found');
    }
  };

  // ... (keep existing create/update booking functions) ...
  const createBooking = async (bookingData) => {
    const action = {
        id: Date.now().toString(),
        type: 'bookings',
        subType: 'create',
        data: bookingData,
        timestamp: new Date().toISOString(),
        synced: false
    };

    if (isOnline) {
        try {
            
            const response = await fetch(`${apiBase()}/api/mobile/bookings`, {
                method: 'POST',
                headers: {
                    ...scopeHeaders(token),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(bookingData)
            });
            
            if (response.ok) {
                // const result = await response.json();
                fetchBookings(); // Refresh list
                setShowNewBooking(false);
                alert('Booking created successfully');
            }
        } catch (error) {
            console.error('Error creating booking:', error);
            saveOfflineAction(action);
            setOfflineActions([...offlineActions, action]);
            alert('Offline: Booking saved and will sync when online');
            setShowNewBooking(false);
        }
    } else {
        saveOfflineAction(action);
        setOfflineActions([...offlineActions, action]);
        alert('Offline: Booking saved and will sync when online');
        setShowNewBooking(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    const action = {
        id: Date.now().toString(),
        type: 'bookings',
        subType: 'update_status',
        bookingId,
        status,
        timestamp: new Date().toISOString(),
        synced: false
    };

    // Optimistic update
    setBookings(bookings.map(b => b.id === bookingId ? { ...b, status } : b));
    if (selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, status });
    }

    if (isOnline) {
        try {
            
            await fetch(`${apiBase()}/api/mobile/bookings/${bookingId}/status`, {
                method: 'PUT',
                headers: {
                    ...scopeHeaders(token),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status })
            });
        } catch (error) {
            console.error('Error updating booking status:', error);
            saveOfflineAction(action);
            setOfflineActions([...offlineActions, action]);
        }
    } else {
        saveOfflineAction(action);
        setOfflineActions([...offlineActions, action]);
    }
  };

  // Renderers
  const renderHousekeeping = () => {
    const housekeepingRooms = rooms.filter(r => 
      ['dirty', 'cleaning', 'inspection'].includes(r.status) || activeView === 'housekeeping'
    );

    return (
      <div className="space-y-4 px-4 py-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-accent-50 p-3 rounded-lg border border-warn/40">
            <p className="text-xs text-accent-600 font-medium uppercase">Dirty</p>
            <p className="font-display text-2xl font-bold text-accent-800">
              {rooms.filter(r => r.status === 'dirty').length}
            </p>
          </div>
          <div className="bg-primary-50 p-3 rounded-lg border border-primary-100">
            <p className="text-xs text-primary-600 font-medium uppercase">Cleaning</p>
            <p className="font-display text-2xl font-bold text-primary-900">
              {rooms.filter(r => r.status === 'cleaning').length}
            </p>
          </div>
        </div>

        {rooms.map(room => (
          <div key={room.id} className="bg-surface rounded-lg shadow-sm p-4 border-l-4 border-transparent" style={{
            borderLeftColor: room.status === 'dirty' ? '#f97316' : room.status === 'ready' ? '#22c55e' : '#3b82f6'
          }}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-ink text-lg">{room.name}</h3>
                <p className="text-sm text-ink-muted">{room.type} • Floor {room.floor}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize
                ${room.status === 'dirty' ? 'bg-accent-100 text-accent-800' : 
                  room.status === 'ready' ? 'bg-primary-100 text-primary-800' : 'bg-primary-100 text-primary-800'}`}>
                {room.status}
              </span>
            </div>

            <div className="mt-4 flex space-x-2">
              {room.status === 'dirty' && (
                <button 
                  onClick={() => updateRoomStatus(room.id, 'cleaning')}
                  className="flex-1 bg-primary-500 text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center"
                >
                  <SprayCan className="w-4 h-4 mr-2" />
                  Start Cleaning
                </button>
              )}
              {room.status === 'cleaning' && (
                <button 
                  onClick={() => updateRoomStatus(room.id, 'ready')}
                  className="flex-1 bg-primary-500 text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Ready
                </button>
              )}
              {room.status === 'ready' && (
                <button 
                  onClick={() => updateRoomStatus(room.id, 'dirty')}
                  className="flex-1 bg-surface-sunken text-ink-muted py-2 rounded-lg text-sm font-medium"
                >
                  Mark Dirty
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderBookingsList = () => {
    // ... existing list rendering logic
    const filteredBookings = bookings.filter(booking => {
        const matchesSearch = booking.guestName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             booking.roomName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="px-4 py-4 space-y-3">
            {filteredBookings.map(booking => {
                // ... existing booking card JSX
                const getStatusColor = (status) => {
                    const colors = {
                      confirmed: 'text-primary-600 bg-primary-100',
                      pending: 'text-accent-600 bg-warn/15',
                      cancelled: 'text-danger bg-danger/10',
                      completed: 'text-primary-600 bg-primary-100'
                    };
                    return colors[status] || 'text-ink-muted bg-surface-sunken';
                };
                
                return (
                    <div key={booking.id} className="bg-surface rounded-lg shadow-sm overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-ink">{booking.guestName}</h3>
                            <p className="text-sm text-ink-muted">{booking.roomName}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-ink-muted">Check-in</p>
                            <p className="font-medium">{new Date(booking.checkIn).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <p className="text-ink-muted">Check-out</p>
                            <p className="font-medium">{new Date(booking.checkOut).toLocaleDateString()}</p>
                          </div>
                        </div>
        
                        <div className="flex items-center justify-between mt-3 pt-3 border-t">
                          <div className="flex items-center space-x-4 text-sm text-ink-muted">
                            <div className="flex items-center space-x-1">
                              <Users className="w-4 h-4" />
                              <span>{booking.guests}</span>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => setSelectedBooking(booking)}
                            className="text-primary-600 hover:text-primary-700"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                );
            })}
        </div>
    );
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div></div>;

  return (
    <div className="min-h-screen bg-surface-muted pb-20">
      {/* Header */}
      <div className="bg-surface shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center mb-3">
            <h1 className="font-display text-xl font-bold text-ink">
              {activeView === 'housekeeping' ? 'Housekeeping' : 'Bookings'}
            </h1>
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveView(activeView === 'bookings' ? 'housekeeping' : 'bookings')}
                className="p-2 bg-surface-sunken rounded-full text-ink-muted hover:bg-surface-border"
              >
                {activeView === 'bookings' ? <Bed className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
              </button>
              {activeView === 'bookings' && (
                <button
                  onClick={() => setShowScanner(true)}
                  className="p-2 bg-primary-100 rounded-full text-primary-600 hover:bg-primary-200"
                >
                  <Scan className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {activeView === 'bookings' && (
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                <input
                  type="text"
                  placeholder="Search guest or room..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-surface-border rounded-lg focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <button
                onClick={() => setShowNewBooking(true)}
                className="flex items-center justify-center w-10 h-10 bg-primary-500 text-white rounded-lg"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Offline Sync Status */}
      {!isOnline && offlineActions.length > 0 && (
        <div className="bg-warn/10 border-b border-warn/40 px-4 py-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-accent-800">
              {offlineActions.length} changes will sync when online
            </p>
            <button
              onClick={() => {/* sync logic */}}
              className="text-sm text-accent-600 underline"
            >
              Sync Now
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      {activeView === 'housekeeping' ? renderHousekeeping() : renderBookingsList()}

      {/* Modals */}
      {showNewBooking && (
        <NewBookingModal
          rooms={rooms}
          onClose={() => setShowNewBooking(false)}
          onSave={createBooking}
        />
      )}

      {selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onUpdate={updateBookingStatus}
        />
      )}

      {showScanner && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-sm aspect-square bg-ink rounded-2xl overflow-hidden relative mb-8 border-2 border-primary-500">
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-white opacity-50">Camera View</p>
            </div>
            {/* Scan animation */}
            <div className="absolute inset-x-0 top-0 h-1 bg-primary-500 shadow-[0_0_10px_rgba(59,130,246,0.5)] animate-[scan_2s_ease-in-out_infinite]"></div>
          </div>
          <p className="text-white text-lg font-medium mb-8">Scan Booking QR Code</p>
          <button 
            onClick={() => setShowScanner(false)}
            className="px-8 py-3 bg-surface text-ink rounded-full font-bold"
          >
            Cancel
          </button>
          
          {/* Mock actions for dev */}
          <div className="mt-8 flex space-x-4">
            <button 
              onClick={() => handleCheckInScan('BK-123')}
              className="text-xs text-ink-muted underline"
            >
              Simulate Scan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ... (keep existing Modal components: NewBookingModal, BookingDetailsModal) ...
const NewBookingModal = ({ rooms, onClose, onSave }) => {
    // ... existing implementation
    const [formData, setFormData] = useState({
        guestName: '',
        roomId: '',
        checkIn: '',
        checkOut: '',
        guests: 1,
        notes: ''
      });
    
      const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
      };
    
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold mb-4">New Booking</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Guest Name</label>
                <input aria-label="Guest Name"
                  type="text"
                  required
                  value={formData.guestName}
                  onChange={(e) => setFormData({...formData, guestName: e.target.value})}
                  className="w-full px-3 py-2 border border-surface-border rounded-lg"
                />
              </div>
    
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Room</label>
                <select aria-label="Room"
                  required
                  value={formData.roomId}
                  onChange={(e) => setFormData({...formData, roomId: e.target.value})}
                  className="w-full px-3 py-2 border border-surface-border rounded-lg"
                >
                  <option value="">Select a room</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </div>
    
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Check-in</label>
                  <input aria-label="Check-in"
                    type="datetime-local"
                    required
                    value={formData.checkIn}
                    onChange={(e) => setFormData({...formData, checkIn: e.target.value})}
                    className="w-full px-3 py-2 border border-surface-border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Check-out</label>
                  <input aria-label="Check-out"
                    type="datetime-local"
                    required
                    value={formData.checkOut}
                    onChange={(e) => setFormData({...formData, checkOut: e.target.value})}
                    className="w-full px-3 py-2 border border-surface-border rounded-lg"
                  />
                </div>
              </div>
    
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Number of Guests</label>
                <input aria-label="Number of Guests"
                  type="number"
                  min="1"
                  required
                  value={formData.guests}
                  onChange={(e) => setFormData({...formData, guests: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-surface-border rounded-lg"
                />
              </div>
    
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Notes</label>
                <textarea aria-label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-surface-border rounded-lg"
                  rows={3}
                />
              </div>
    
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2 border border-surface-border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-primary-500 text-white rounded-lg"
                >
                  Create Booking
                </button>
              </div>
            </form>
          </div>
        </div>
      );
};

const BookingDetailsModal = ({ booking, onClose, onUpdate }) => {
    // ... existing implementation
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg font-semibold">Booking Details</h2>
              <button onClick={onClose} className="text-ink-subtle hover:text-ink-muted">
                <X className="w-5 h-5" />
              </button>
            </div>
    
            <div className="space-y-4">
              <div>
                <p className="text-sm text-ink-muted">Guest</p>
                <p className="font-medium">{booking.guestName}</p>
              </div>
              
              <div>
                <p className="text-sm text-ink-muted">Room</p>
                <p className="font-medium">{booking.roomName}</p>
              </div>
    
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-ink-muted">Check-in</p>
                  <p className="font-medium">{new Date(booking.checkIn).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-ink-muted">Check-out</p>
                  <p className="font-medium">{new Date(booking.checkOut).toLocaleString()}</p>
                </div>
              </div>
    
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-ink-muted">Guests</p>
                  <p className="font-medium">{booking.guests}</p>
                </div>
                <div>
                  <p className="text-sm text-ink-muted">Status</p>
                  <p className="font-medium capitalize">{booking.status}</p>
                </div>
              </div>
    
              {booking.notes && (
                <div>
                  <p className="text-sm text-ink-muted">Notes</p>
                  <p className="font-medium">{booking.notes}</p>
                </div>
              )}
            </div>
    
            <div className="flex space-x-3 mt-6">
              <button
                onClick={onClose}
                className="flex-1 py-2 border border-surface-border rounded-lg"
              >
                Close
              </button>
              {booking.status === 'pending' && (
                <>
                  <button
                    onClick={() => onUpdate(booking.id, 'confirmed')}
                    className="flex-1 py-2 bg-primary-500 text-white rounded-lg"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => onUpdate(booking.id, 'cancelled')}
                    className="flex-1 py-2 bg-danger text-white rounded-lg"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
};

export default MobileBookings;
